/**
 * PRP-MCP-8 work item 3 (founder decisions F1, F2): replaces the Download ESX
 * control when the estimate is chat work (created in chat, or an import with a
 * chat change) and the account has no subscription, role or grant. The text and
 * labels come from the server (`export_lock` on GET /imports/{hash},
 * app/data/export_gate.json); the link targets are the access lane's
 * TRIAL_PATH and SUBSCRIBE_PATH (McpConnectSection), the same values the server
 * reads from app/mcp/data/access.json. An imported estimate keeps its Original
 * export under the credit rules.
 *
 * Work item 10: records `view` once per estimate when shown and `click` with the
 * link's target, best effort (an event never blocks the link).
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { BackendAPI } from '../../lib/api';
import type { ExportLock } from '../../types/exportSummary';
import { Button } from './ui/button';
import { IconDownload } from './icons';
import { SUBSCRIBE_PATH, TRIAL_PATH } from './McpConnectSection';

export interface ExportLockedPanelProps {
  lock: ExportLock;
  parseHash: string;
  /** Set when an Original is stored for this import: exports it (credit rules). */
  onExportOriginal?: () => void;
  busy?: boolean;
}

function record(parseHash: string, event: 'view' | 'click', target?: 'trial' | 'subscribe') {
  void Promise.resolve()
    .then(() => BackendAPI.recordExportLockedEvent(parseHash, event, target))
    .catch(() => {});
}

export default function ExportLockedPanel({ lock, parseHash, onExportOriginal, busy }: ExportLockedPanelProps) {
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (seen.current === parseHash) return;
    seen.current = parseHash;
    record(parseHash, 'view');
  }, [parseHash]);

  return (
    <div
      className="flex flex-wrap items-center justify-end gap-2 rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-2)] px-3 py-2"
      data-testid="export-locked-panel"
    >
      <p className="text-[12px] text-[var(--tx-mid)]">{lock.message}</p>
      <Button size="sm" variant="primary" asChild>
        <Link to={TRIAL_PATH} onClick={() => record(parseHash, 'click', 'trial')} data-testid="export-locked-trial">
          {lock.trial_label}
        </Link>
      </Button>
      <Button size="sm" asChild>
        <Link
          to={SUBSCRIBE_PATH}
          onClick={() => record(parseHash, 'click', 'subscribe')}
          data-testid="export-locked-subscribe"
        >
          {lock.subscribe_label}
        </Link>
      </Button>
      {onExportOriginal && (
        <Button
          size="sm"
          onClick={onExportOriginal}
          disabled={busy}
          title="Export the estimate as it was imported, before any chat change"
          data-testid="export-original"
        >
          <IconDownload />
          {busy ? 'Exporting…' : 'Download Original ESX'}
        </Button>
      )}
    </div>
  );
}
